import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ManageDelegatesComponent,
  PLATFORM_ADMIN_DELEGATE_SCOPE_OPTIONS,
  type ManageOwnerRow,
  type RequestOwnerPayload,
} from '@angular-workspace/feature';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { AdminSidebarComponent } from '../sidebar/admin-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { ToasterService } from '../../services/toaster.service';
import { WorkspaceContextService } from '../../services/workspace-context.service';

@Component({
  selector: 'app-admin-delegate',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    AdminSidebarComponent,
    PageHeaderComponent,
    ManageDelegatesComponent,
  ],
  templateUrl: './delegate.component.html',
  styleUrl: './delegate.component.scss',
})
export class AdminDelegateComponent {
  private readonly toaster = inject(ToasterService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly workspaceContext = inject(WorkspaceContextService);

  readonly ownersOnly = !!this.route.snapshot.data['ownersOnly'];
  readonly platformAdminScopeOptions = PLATFORM_ADMIN_DELEGATE_SCOPE_OPTIONS;

  get activeDelegatedOwnerId(): string | null {
    return this.workspaceContext.getActiveDelegationId();
  }

  onDelegateCreated(): void {
    this.toaster.success('Delegate created successfully');
  }

  onDelegateDeleted(): void {
    this.toaster.success('Delegate deleted successfully');
  }

  onAccessRevoked(row: ManageOwnerRow): void {
    const next = this.workspaceContext.exitDelegatedOwner(row.id);
    this.toaster.success(
      next
        ? 'Delegated access revoked. Switched back to your workspace.'
        : 'Delegated access revoked successfully',
    );
    if (next) {
      this.router.navigate([next]).catch((err) => {
        console.error('Failed to navigate after revoke:', err);
      });
    }
  }

  onSwitchOwner(row: ManageOwnerRow): void {
    const workspace = this.workspaceContext.adoptOwnerWorkspace(row);
    const route = this.workspaceContext.panelRouteFor(workspace);
    this.toaster.success(`Switched to ${workspace.name}'s workspace`);
    this.router.navigate([route]).catch((err) => {
      console.error('Failed to navigate after owner switch:', err);
    });
  }

  onRequestOwner(payload: RequestOwnerPayload): void {
    const name = (payload.owner.name || '').trim() || payload.owner.email;
    const requested = payload.requestScopes?.length
      ? payload.requestScopes
      : payload.scopes;
    const revoked = payload.revokeScopes ?? [];
    const parts: string[] = [];
    if (requested.length) {
      parts.push(`request ${requested.join(', ')}`);
    }
    if (revoked.length) {
      parts.push(`revoke ${revoked.join(', ')}`);
    }
    this.toaster.success(`Sent to ${name}: ${parts.join('; ')}`);
  }
}
